import { parseDbTimestamp } from "@repo/shared";
import type {
  ComplianceCheckStatus,
  ComplianceFlowRow,
  CompliancePeriod,
  ComplianceReviewAction,
  RiskDeviationStanding,
  TextKey,
} from "@repo/shared/types";
import { ListFilter, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { SearchInput } from "@/components/ui/search-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  rowControl,
  SortableTableHead,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowLink,
} from "@/components/ui/table";
import { TablePagination } from "@/components/ui/table-pagination";
import {
  useClientIdentity,
  VerifiedIdBadge,
  VerifiedIdDialog,
  VerifiedIdSubjects,
} from "@/components/verified-id";
import { useTableSort } from "@/hooks/use-table-sort";
import { useMe } from "@/lib/hooks/use-clients";
import {
  useComplianceReviewLog,
  useFlowsForReview,
  useRecordComplianceCheck,
  useRecordComplianceChecks,
} from "@/lib/hooks/use-compliance";
import { usePagination } from "@/lib/hooks/use-pagination";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { hasPermission, type Permission } from "@/lib/permissions";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { displayUserName } from "@/lib/user-display";
import { cn } from "@/lib/utils";
import { CheckDialog } from "./check-dialog";
import { T } from "./page.i18n";

/**
 * An added-but-unchosen filter. There is deliberately no "All" option on these:
 * the column each one filters is no longer in the table, so "All" would say
 * nothing and only re-clutter the bar. Not narrowing is what removing the
 * filter (its ×) means — Period is the exception, since it always bounds the
 * server query and therefore always has a value.
 */
const UNSET = "";

// Sentinel for "no advisor on the flow instance". Radix Select rejects an empty
// string value, so an unassigned flow needs a stand-in that is not a user id.
const NO_ADVISOR = "__unassigned__";

/**
 * The filters a reviewer can add. Search and Period are not here: search is how
 * you find a single row, and Period bounds the SERVER query — hiding either
 * would hide why rows are missing rather than merely tidying the bar.
 */
const OPTIONAL_FILTERS = [
  "advisor",
  "flow",
  "risk",
  // QL-375 — the mandate deviation standing, beside the risk-bound one.
  "mandate",
  "custom",
  "sustainability",
  "amount",
  "check",
] as const;

type OptionalFilter = (typeof OPTIONAL_FILTERS)[number];

const PERIODS = new Set<CompliancePeriod>([
  "30d",
  "90d",
  "12m",
  "this_month",
  "this_quarter",
  "this_year",
  "all",
]);

/** The check filter: one status, or "not approved" — not reviewed and flagged together. */
type CheckFilter = ComplianceCheckStatus | "not_approved";
const CHECK_FILTERS = new Set<CheckFilter>(["not_reviewed", "flagged", "approved", "not_approved"]);

/** What each optional filter is called in the "Add filter" menu. */
const FILTER_LABEL: Record<OptionalFilter, TextKey<typeof T.en_GB>> = {
  advisor: "advisor",
  flow: "flowDefinition",
  risk: "riskDeviation",
  mandate: "mandateDeviation",
  custom: "customPortfolioFilter",
  sustainability: "sustainability",
  amount: "minAmount",
  check: "checkStatus",
};

/**
 * A filter the reviewer added: its label, its control, and the × that resets the
 * value and takes it off the bar again.
 *
 * Private to this page on purpose — it is a layout wrapper, not a component
 * anything else would reuse.
 */
function FilterField({
  label,
  title,
  removeLabel,
  onRemove,
  children,
}: {
  label: string;
  title?: string;
  removeLabel: string;
  onRemove: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <span className="text-sm text-muted-foreground" title={title}>
          {label}
        </span>
        <button
          type="button"
          onClick={onRemove}
          aria-label={removeLabel}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
      {children}
    </div>
  );
}

/** Review standing → badge variant. `flagged` is the one that still needs work. */
const CHECK_VARIANT: Record<
  ComplianceCheckStatus,
  "secondary" | "success" | "warning" | "destructive"
> = {
  not_reviewed: "secondary",
  flagged: "warning",
  approved: "success",
};

/** One sortable row per flow instance: the row, plus the values the cells render. */
type Row = {
  flow: ComplianceFlowRow;
  client: string;
  name: string;
  advisor: string;
  completed: string;
  /** Epoch ms behind the rendered date, so the column sorts chronologically. */
  completedTs: number;
  /** Compliance-check standing, as a sort key and a cell value. */
  check: ComplianceCheckStatus;
  /** Numeric, so the column sorts by value rather than by formatted string. */
  amount: number;
};

export default function ComplianceFlowsReviewPage() {
  const navigate = useNavigate();
  const { formatDate, formatDateTime, formatNumber } = useRegionalFormat();
  const { me, isLoading: meLoading } = useMe();
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  // QL-365 — a deleted advisor keeps their flows; the name says they are gone.
  const advisorLabel = useCallback(
    (f: ComplianceFlowRow) =>
      displayUserName({ name: f.advisorName, deletedAt: f.advisorDeletedAt }, userLanguage) ||
      t.unassigned,
    [userLanguage, t],
  );
  const userPermissions = (me?.permissions || ["advisor"]) as Permission[];
  const allowed = hasPermission(userPermissions, "compliance");

  // QL-357 — `?period=` and `?check=` seed the period and the check filter, so
  // the dashboard's Review status and Advice to check can open the list on
  // exactly what they counted. Read once as the initial value; changing either
  // afterwards drops it from the URL, so a reload never re-applies a filter
  // the reviewer has since changed.
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState<CompliancePeriod>(() => {
    const raw = searchParams.get("period");
    return raw && PERIODS.has(raw as CompliancePeriod) ? (raw as CompliancePeriod) : "90d";
  });
  const dropSeed = (key: "period" | "check") => {
    if (!searchParams.has(key)) return;
    searchParams.delete(key);
    setSearchParams(searchParams, { replace: true });
  };
  // QL-e — one dialog for the table, keyed by the row that opened it.
  const [identityFor, setIdentityFor] = useState<string | null>(null);
  const { identity: identityDetail } = useClientIdentity(identityFor);
  const [advisorId, setAdvisorId] = useState<string>(UNSET);
  const [search, setSearch] = useState("");
  const [checkFilter, setCheckFilter] = useState<CheckFilter | "">(() => {
    const raw = searchParams.get("check");
    return raw && CHECK_FILTERS.has(raw as CheckFilter) ? (raw as CheckFilter) : UNSET;
  });
  const [flowFilter, setFlowFilter] = useState<string>(UNSET);
  const [riskFilter, setRiskFilter] = useState<RiskDeviationStanding | "">(UNSET);
  const [mandateFilter, setMandateFilter] = useState<RiskDeviationStanding | "">(UNSET);
  const [customFilter, setCustomFilter] = useState<"" | "yes" | "no">(UNSET);
  const [sustainabilityFilter, setSustainabilityFilter] = useState<
    "" | "yes" | "no" | "unanswered"
  >(UNSET);
  const [minAmount, setMinAmount] = useState("");
  // Thematic selection: the flows the reviewer has ticked to freeze as a batch.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reviewing, setReviewing] = useState<ComplianceFlowRow | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);
  /** Bumped on each recorded action, so the dialog knows to clear its note box. */
  const [recorded, setRecorded] = useState(0);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [pickupNote, setPickupNote] = useState("");
  const [sampleSize, setSampleSize] = useState("10");
  /**
   * How the current selection started — a drawn sample, or picked by hand.
   *
   * A spot check must be a sample of the pool, not of whatever the reviewer had
   * already ticked, so once they start choosing rows themselves the draw is
   * taken away rather than left there to silently replace their work. Back to
   * "none" the moment the selection empties, which puts the tool back.
   */
  const [selectionOrigin, setSelectionOrigin] = useState<"none" | "manual" | "draw">("none");

  // Which optional filters are on the bar. Seeded from whatever already has a
  // non-default value, so a deep link (?batch=) arrives with its filter shown
  // rather than silently applied — an invisible filter that drops rows is worse
  // than a cluttered bar.
  const [shownFilters, setShownFilters] = useState<Set<OptionalFilter>>(
    () => new Set(checkFilter !== UNSET ? ["check"] : []),
  );

  /** Reset a filter to "everything" and take it off the bar. */
  const RESET: Record<OptionalFilter, () => void> = {
    advisor: () => setAdvisorId(UNSET),
    flow: () => setFlowFilter(UNSET),
    risk: () => setRiskFilter(UNSET),
    mandate: () => setMandateFilter(UNSET),
    custom: () => setCustomFilter(UNSET),
    sustainability: () => setSustainabilityFilter(UNSET),
    amount: () => setMinAmount(""),
    check: () => {
      setCheckFilter(UNSET);
      dropSeed("check");
    },
  };

  const showFilter = (key: OptionalFilter) => setShownFilters((prev) => new Set(prev).add(key));

  const hideFilter = (key: OptionalFilter) => {
    RESET[key]();
    setShownFilters((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const unusedFilters = OPTIONAL_FILTERS.filter((k) => !shownFilters.has(k));
  const recordCheck = useRecordComplianceCheck();
  const reviewLog = useComplianceReviewLog(reviewing?.flowInstanceId ?? null);
  const recordChecks = useRecordComplianceChecks();

  useEffect(() => {
    if (!meLoading && !allowed) navigate("/", { replace: true });
  }, [meLoading, allowed, navigate]);

  const { data: flows, isLoading } = useFlowsForReview(period, !meLoading && allowed);

  // Advisor options come from the rows in hand rather than a second endpoint, so
  // the list only ever offers advisors who actually have flows in the period.
  const advisors = [
    ...new Map((flows ?? []).map((f) => [f.advisorId ?? NO_ADVISOR, advisorLabel(f)])),
  ].sort((a, b) => a[1].localeCompare(b[1]));

  // The period filter is server-side (it bounds the query); the advisor and
  // name filters are cheap matches over rows already fetched.
  //
  // The search covers the client AND the flow's own name, because both are
  // "the name" of a row here — a reviewer looking for a client's flow and one
  // looking for a named flow instance are the same search box.
  const query = search.trim().toLowerCase();
  // Blank or nonsense means "no floor" rather than 0 — a 0 floor would silently
  // drop every flow with no amounts recorded.
  const parsedFloor = Number(minAmount.trim());
  const amountFloor = minAmount.trim() !== "" && Number.isFinite(parsedFloor) ? parsedFloor : null;
  // Memoised on the inputs rather than rebuilt per render: `useTableSort` and
  // `usePagination` both key their own memos on the array identity, so a fresh
  // array every render would re-sort and re-slice the whole list on every
  // keystroke in an unrelated field (the pickup note, the sample size).
  const rows: Row[] = useMemo(
    () =>
      (flows ?? [])
        .filter((f) => advisorId === UNSET || (f.advisorId ?? NO_ADVISOR) === advisorId)
        .filter(
          (f) =>
            query === "" ||
            f.clientName.toLowerCase().includes(query) ||
            (f.flowInstanceName || f.flowName).toLowerCase().includes(query),
        )
        .filter((f) => flowFilter === UNSET || f.flowName === flowFilter)
        .filter((f) => riskFilter === UNSET || f.riskDeviation === riskFilter)
        .filter((f) => mandateFilter === UNSET || f.mandateDeviation === mandateFilter)
        .filter((f) =>
          customFilter === UNSET
            ? true
            : customFilter === "yes"
              ? f.hasCustomPortfolio
              : !f.hasCustomPortfolio,
        )
        .filter((f) => {
          if (sustainabilityFilter === UNSET) return true;
          // Tri-state: "unanswered" is null, which is neither yes nor no — a client
          // who was never asked must not be counted as having declined.
          if (sustainabilityFilter === "unanswered") return f.sustainabilityImportant === null;
          return f.sustainabilityImportant === (sustainabilityFilter === "yes");
        })
        .filter((f) => amountFloor === null || f.totalAmount > amountFloor)
        .filter((f) =>
          checkFilter === UNSET
            ? true
            : checkFilter === "not_approved"
              ? f.complianceCheck.status !== "approved"
              : f.complianceCheck.status === checkFilter,
        )
        .map((f) => {
          // The list is completed-only and the period filter bounds on completion,
          // so the completion date is the one that matches what was asked for. The
          // fallback is defensive: the service already excludes a null completedAt.
          // Parsed via the helper so the bare Postgres timestamp reads as UTC
          // rather than local time, which would shift it across midnight.
          const completedAt = parseDbTimestamp(f.completedAt);
          return {
            flow: f,
            client: f.clientName,
            name: f.flowInstanceName || f.flowName,
            advisor: advisorLabel(f),
            completed: completedAt ? formatDate(completedAt) : "—",
            completedTs: completedAt?.getTime() ?? 0,
            check: f.complianceCheck.status,
            amount: f.totalAmount,
          };
        }),
    [
      flows,
      advisorId,
      query,
      flowFilter,
      riskFilter,
      mandateFilter,
      customFilter,
      sustainabilityFilter,
      amountFloor,
      checkFilter,
      t,
      formatDate,
      advisorLabel,
    ],
  );

  const { sorted, directionFor, toggleSort } = useTableSort(rows, "completedTs", "desc");
  // What a spot check may draw from, and what the Mark action may touch: the
  // filtered list minus the rows already flagged. The service returns
  // completed advice only, but it refuses to flag a flow that is already in
  // the phase — so an already-flagged row in the draw would either shrink the
  // sample silently or, if every drawn row were flagged, fail the whole batch.
  const canFlag = (status: ComplianceCheckStatus) => status !== "flagged";
  const eligible = sorted.filter((r) => canFlag(r.check));
  const eligibleCount = eligible.length;
  // An empty or zero size would draw nothing and look like a dead button, so it
  // disables instead.
  const sampleSizeValid = Number.isInteger(Number(sampleSize)) && Number(sampleSize) > 0;
  const { page, setPage, pageSize, totalCount, totalPages, pageItems } = usePagination(sorted, 25);

  // See the clients review page: clamping to the last surviving page leaves the
  // reviewer on an empty table after a filter narrows the list.
  useEffect(() => {
    setPage(1);
  }, [
    period,
    advisorId,
    query,
    checkFilter,
    flowFilter,
    riskFilter,
    mandateFilter,
    customFilter,
    sustainabilityFilter,
    amountFloor,
    setPage,
  ]);

  // The selection follows the list. A row ticked and then filtered out (a
  // narrower period, an added filter, a search, a refetch that dropped it)
  // would otherwise stay in the set and be flagged by "Flag N for review" —
  // a row the reviewer can no longer see, under a count that does not match
  // the table. So anything the filters no longer leave is un-ticked. Keyed on
  // the sorted ids rather than `rows`, which is rebuilt every render.
  const visibleKey = rows
    .map((r) => r.flow.flowInstanceId)
    .sort()
    .join("\n");
  useEffect(() => {
    const visible = new Set(visibleKey === "" ? [] : visibleKey.split("\n"));
    const kept = [...selected].filter((id) => visible.has(id));
    if (kept.length === selected.size) return;
    setSelected(new Set(kept));
    // Same rule as clearing by hand: an emptied selection puts the draw back.
    if (kept.length === 0) setSelectionOrigin("none");
  }, [visibleKey, selected]);

  // Flow definitions present in the rows in hand, like the advisor list.
  const flowNames = [...new Set((flows ?? []).map((f) => f.flowName))].sort((a, b) =>
    a.localeCompare(b),
  );

  // Falls back to the raw value: the column is a varchar, so a row written by
  // an older shape of this feature must still render as something rather than
  // as an empty cell that looks like a bug. Flagging it from the list puts it
  // back on a known standing.
  const checkLabel = (status: ComplianceCheckStatus) =>
    ({
      not_reviewed: t.checkNotReviewed,
      flagged: t.checkFlagged,
      approved: t.checkApproved,
    })[status] ?? String(status).replaceAll("_", " ");

  /**
   * The spot check: tick N of the rows the filters currently leave, at random.
   *
   * Deliberately client-side over the filtered set rather than a server-side
   * sample. The reviewer can already see and narrow the pool, so the draw has
   * nothing to add beyond picking — and what is actually recorded is each
   * flow's own verdict and log entry, not a sample object to keep in step.
   *
   * Only completed advice not already flagged is eligible, matching the
   * checkbox the table renders and the pool the server enforces on save.
   */
  const selectRandom = () => {
    const pool = eligible.map((r) => r.flow.flowInstanceId);

    const wanted = Number(sampleSize);
    const take = Math.min(Number.isInteger(wanted) && wanted > 0 ? wanted : 0, pool.length);
    if (take === 0) return;

    // Fisher–Yates over a copy: sorting by Math.random() is biased, and a
    // compliance sample is the last place to be casual about that.
    const shuffled = [...pool];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setSelected(new Set(shuffled.slice(0, take)));
    setSelectionOrigin("draw");
    // No "N drawn" message: the selection bar below counts the live selection,
    // and a remembered number goes stale the moment a checkbox is touched.
    setBatchError(null);
  };

  const toggleSelected = (flowInstanceId: string) => {
    setBatchError(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(flowInstanceId)) next.delete(flowInstanceId);
      else next.add(flowInstanceId);

      // Emptying the selection by hand returns it to nobody, so the draw comes
      // back. Otherwise the first hand-picked row claims it; a draw that is
      // then adjusted stays a draw.
      setSelectionOrigin((origin) => {
        if (next.size === 0) return "none";
        return origin === "none" ? "manual" : origin;
      });
      return next;
    });
  };

  const submitCheck = async (action: ComplianceReviewAction, notes: string) => {
    if (!reviewing) return;
    setCheckError(null);
    try {
      await recordCheck.mutateAsync({ flowInstanceId: reviewing.flowInstanceId, action, notes });
      // A remark is one of possibly many, so the review stays open and the note
      // box is cleared for the next. Approving ends the phase — there is
      // nothing further to do in there, so it closes.
      if (action === "approve") setReviewing(null);
      else setRecorded((n) => n + 1);
    } catch (err) {
      setCheckError(err instanceof Error ? err.message : t.checkFailed);
    }
  };

  /**
   * Flag the ticked rows for review — the only way a flow enters the phase.
   *
   * The note is mandatory, exactly as for a single verdict — this IS a verdict,
   * just the first one, and each flow's activity log records it as such.
   */
  const markSelected = async () => {
    setBatchError(null);
    try {
      const result = await recordChecks.mutateAsync({
        flowInstanceIds: [...selected],
        notes: pickupNote.trim(),
      });
      toast.success(t.markSelectedDone(String(result.updated)));
      setSelected(new Set());
      setSelectionOrigin("none");
      setPickupNote("");
    } catch (err) {
      setBatchError(err instanceof Error ? err.message : t.markSelectedFailed);
    }
  };

  if (meLoading || !allowed) return null;

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: t.compliance, href: "/compliance" }, { label: t.title }]}
        title={t.title}
        description={t.description}
      />

      {/* Period and Search are always on; everything else is opt-in. Search sits
          last among the controls, as it does on the clients review, so the bar
          reads: what is listed, how it is narrowed, then finding one row.
          Nine controls at once read as one flat wall, and a given review uses
          one or two of them — so a filter appears when it is asked for and
          disappears, reset, when its × is pressed. */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <span className="text-sm text-muted-foreground">{t.period}</span>
          <Select
            value={period}
            onValueChange={(v) => {
              setPeriod(v as CompliancePeriod);
              dropSeed("period");
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30d">{t.period30d}</SelectItem>
              <SelectItem value="90d">{t.period90d}</SelectItem>
              <SelectItem value="12m">{t.period12m}</SelectItem>
              <SelectItem value="this_month">{t.periodThisMonth}</SelectItem>
              <SelectItem value="this_quarter">{t.periodThisQuarter}</SelectItem>
              <SelectItem value="this_year">{t.periodThisYear}</SelectItem>
              <SelectItem value="all">{t.periodAll}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {shownFilters.has("advisor") && (
          <FilterField
            label={t.advisor}
            removeLabel={t.removeFilter}
            onRemove={() => hideFilter("advisor")}
          >
            <Select value={advisorId} onValueChange={setAdvisorId}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder={t.choose} />
              </SelectTrigger>
              <SelectContent>
                {advisors.map(([id, name]) => (
                  <SelectItem key={id} value={id}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
        )}

        {shownFilters.has("flow") && (
          <FilterField
            label={t.flowDefinition}
            removeLabel={t.removeFilter}
            onRemove={() => hideFilter("flow")}
          >
            <Select value={flowFilter} onValueChange={setFlowFilter}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder={t.choose} />
              </SelectTrigger>
              <SelectContent>
                {flowNames.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
        )}

        {shownFilters.has("risk") && (
          <FilterField
            label={t.riskDeviation}
            title={t.riskHint}
            removeLabel={t.removeFilter}
            onRemove={() => hideFilter("risk")}
          >
            <Select
              value={riskFilter}
              onValueChange={(v) => setRiskFilter(v as RiskDeviationStanding | "")}
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder={t.choose} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="do_not_proceed">{t.riskRefused}</SelectItem>
                <SelectItem value="accepted">{t.riskAccepted}</SelectItem>
                <SelectItem value="none">{t.riskNone}</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>
        )}

        {shownFilters.has("mandate") && (
          <FilterField
            label={t.mandateDeviation}
            title={t.mandateHint}
            removeLabel={t.removeFilter}
            onRemove={() => hideFilter("mandate")}
          >
            <Select
              value={mandateFilter}
              onValueChange={(v) => setMandateFilter(v as RiskDeviationStanding | "")}
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder={t.choose} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="do_not_proceed">{t.riskRefused}</SelectItem>
                <SelectItem value="accepted">{t.riskAccepted}</SelectItem>
                <SelectItem value="none">{t.riskNone}</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>
        )}

        {shownFilters.has("custom") && (
          <FilterField
            label={t.customPortfolioFilter}
            removeLabel={t.removeFilter}
            onRemove={() => hideFilter("custom")}
          >
            <Select
              value={customFilter}
              onValueChange={(v) => setCustomFilter(v as "" | "yes" | "no")}
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder={t.choose} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">{t.yesLabel}</SelectItem>
                <SelectItem value="no">{t.noLabel}</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>
        )}

        {shownFilters.has("sustainability") && (
          <FilterField
            label={t.sustainability}
            removeLabel={t.removeFilter}
            onRemove={() => hideFilter("sustainability")}
          >
            <Select
              value={sustainabilityFilter}
              onValueChange={(v) => setSustainabilityFilter(v as "" | "yes" | "no" | "unanswered")}
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder={t.choose} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">{t.sustainabilityYes}</SelectItem>
                <SelectItem value="no">{t.sustainabilityNo}</SelectItem>
                <SelectItem value="unanswered">{t.sustainabilityUnanswered}</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>
        )}

        {shownFilters.has("amount") && (
          <FilterField
            label={t.minAmount}
            title={t.totalAmountHint}
            removeLabel={t.removeFilter}
            onRemove={() => hideFilter("amount")}
          >
            <Input
              type="number"
              inputMode="numeric"
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
              className="w-36"
            />
          </FilterField>
        )}

        {shownFilters.has("check") && (
          <FilterField
            label={t.checkStatus}
            removeLabel={t.removeFilter}
            onRemove={() => hideFilter("check")}
          >
            <Select
              value={checkFilter}
              onValueChange={(v) => {
                setCheckFilter(v as CheckFilter | "");
                dropSeed("check");
              }}
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder={t.choose} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="not_reviewed">{t.checkNotReviewed}</SelectItem>
                <SelectItem value="flagged">{t.checkFlagged}</SelectItem>
                <SelectItem value="approved">{t.checkApproved}</SelectItem>
                <SelectItem value="not_approved">{t.checkNotApproved}</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>
        )}

        <div className="space-y-1">
          <span className="text-sm text-muted-foreground">{t.search}</span>
          <SearchInput
            className="w-64"
            value={search}
            onValueChange={setSearch}
            placeholder={t.searchPlaceholder}
          />
        </div>

        {unusedFilters.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <ListFilter className="h-4 w-4" />
                {t.addFilter}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {unusedFilters.map((key) => (
                <DropdownMenuItem key={key} onSelect={() => showFilter(key)}>
                  {t[FILTER_LABEL[key]]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Two distinct things, deliberately on separate lines. The top row is a
          TOOL that produces a selection; the bottom row acts on whatever is
          selected, however it got there — drawn, ticked by hand, or both. The
          count always comes from the live selection, never from a remembered
          draw, so the two can never disagree. */}
      <div className="space-y-3">
        {selectionOrigin !== "manual" && (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-muted-foreground">{t.randomLabel}</span>
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              value={sampleSize}
              onChange={(e) => setSampleSize(e.target.value)}
              className="w-20"
              aria-label={t.randomLabel}
            />
            <Button
              variant="outline"
              onClick={selectRandom}
              disabled={eligibleCount === 0 || !sampleSizeValid}
            >
              {t.randomButton}
            </Button>
          </div>
        )}
        {batchError && <p className="text-sm text-destructive">{batchError}</p>}

        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-3">
            <span className="text-sm font-medium">{t.selectedCount(String(selected.size))}</span>
            <Input
              className="w-80"
              placeholder={t.pickupNotePlaceholder}
              value={pickupNote}
              onChange={(e) => setPickupNote(e.target.value)}
            />
            <Button
              onClick={markSelected}
              disabled={recordChecks.isPending || pickupNote.trim() === ""}
            >
              {t.markSelected(String(selected.size))}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setSelected(new Set());
                setSelectionOrigin("none");
                setBatchError(null);
              }}
            >
              {t.clearSelection}
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : sorted.length === 0 ? (
        <EmptyState>{t.empty}</EmptyState>
      ) : (
        <Table variant="navigation">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10" />
              <SortableTableHead
                direction={directionFor("client")}
                onSort={() => toggleSort("client")}
              >
                {t.client}
              </SortableTableHead>
              <SortableTableHead direction={directionFor("name")} onSort={() => toggleSort("name")}>
                {t.flow}
              </SortableTableHead>
              <SortableTableHead
                direction={directionFor("advisor")}
                onSort={() => toggleSort("advisor")}
              >
                {t.advisor}
              </SortableTableHead>
              <SortableTableHead
                direction={directionFor("completedTs")}
                onSort={() => toggleSort("completedTs")}
              >
                {t.completedAt}
              </SortableTableHead>
              <SortableTableHead
                direction={directionFor("amount")}
                onSort={() => toggleSort("amount")}
                title={t.totalAmountHint}
              >
                {t.totalAmount}
              </SortableTableHead>
              <SortableTableHead
                direction={directionFor("check")}
                onSort={() => toggleSort("check")}
              >
                {t.checkStatus}
              </SortableTableHead>
              <TableHead className="text-right">{t.actions}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageItems.map((row) => (
              <TableRow key={row.flow.flowInstanceId}>
                <TableCell className={cn("w-10", rowControl)}>
                  <Checkbox
                    checked={selected.has(row.flow.flowInstanceId)}
                    onCheckedChange={() => toggleSelected(row.flow.flowInstanceId)}
                    // An already-flagged flow cannot be flagged again, so the
                    // server would skip it; offering the box would only make
                    // the "N marked" count disagree with the selection.
                    disabled={!canFlag(row.check)}
                    // Not `t.review`: the Review button in the same row carries
                    // that name, so every row exposed two controls a screen
                    // reader announced identically. Found by CodeRabbit CLI.
                    aria-label={`${t.selectRow}: ${row.client} — ${row.name}`}
                  />
                </TableCell>
                <TableCell className="font-medium">
                  {/* The badge button sits beside (not inside) the row link, above its overlay. */}
                  <span className="inline-flex items-center gap-1.5 [&>button]:relative [&>button]:z-10">
                    <TableRowLink
                      to={`/clients/${row.flow.clientId}/flows/${row.flow.flowInstanceId}`}
                    >
                      {row.client}
                    </TableRowLink>
                    <VerifiedIdBadge
                      summary={row.flow.identity}
                      onOpen={() => setIdentityFor(row.flow.clientId)}
                    />
                  </span>
                  {/* QL-654 - several representatives: who has verified, who is missing. */}
                  <VerifiedIdSubjects summary={row.flow.identity} />
                </TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.advisor}</TableCell>
                <TableCell>{row.completed}</TableCell>
                <TableCell className="tabular-nums">{formatNumber(row.amount)}</TableCell>
                <TableCell>
                  {row.check === "not_reviewed" ? (
                    <span className="text-muted-foreground">{t.checkNotReviewed}</span>
                  ) : (
                    <Badge variant={CHECK_VARIANT[row.check]}>{checkLabel(row.check)}</Badge>
                  )}
                </TableCell>
                <TableCell className={cn("text-right", rowControl)}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCheckError(null);
                      setReviewing(row.flow);
                    }}
                  >
                    {t.review}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* The list's own size, under the list. TablePagination hides itself below
          one page, so without this a reviewer has no count at all until there
          are 26 rows. */}
      {!isLoading && sorted.length > 0 && (
        <p className="text-sm text-muted-foreground">
          {(totalCount === 1 ? t.rowCountOne : t.rowCount)(String(totalCount))}
        </p>
      )}

      <TablePagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={setPage}
      />

      <CheckDialog
        row={reviewing}
        open={reviewing !== null}
        onClose={() => setReviewing(null)}
        onSubmit={submitCheck}
        saving={recordCheck.isPending}
        error={checkError}
        log={reviewLog.data?.entries ?? []}
        logLoading={reviewLog.isLoading}
        recorded={recorded}
        formatDateTime={formatDateTime}
      />

      <VerifiedIdDialog
        open={identityFor !== null}
        onClose={() => setIdentityFor(null)}
        verifications={identityDetail?.verifications ?? []}
      />
    </div>
  );
}
